"use client"

import type React from "react"

import { useId, useState } from "react"
import { ChevronRight, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"

interface CollapsibleInfoProps {
  title: string
  children: React.ReactNode
  icon?: React.ReactNode
  defaultOpen?: boolean
  className?: string
  titleClassName?: string
  contentClassName?: string
  /** Optional callback fired after the open state toggles. */
  onOpenChange?: (open: boolean) => void
}

export function CollapsibleInfo({
  title,
  children,
  icon,
  defaultOpen = false,
  className,
  titleClassName,
  contentClassName,
  onOpenChange,
}: CollapsibleInfoProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const contentId = useId()

  const handleToggle = () => {
    const next = !isOpen
    setIsOpen(next)
    onOpenChange?.(next)
  }

  return (
    <div className={cn("border rounded-lg overflow-hidden", className)}>
      <button
        type="button"
        onClick={handleToggle}
        className={cn(
          "w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors",
          titleClassName,
        )}
        aria-expanded={isOpen}
        aria-controls={contentId}
      >
        <div className="flex items-center gap-3">
          {icon}
          <span className="font-medium">{title}</span>
        </div>
        <div className="flex-shrink-0">
          {isOpen ? (
            <ChevronDown className="w-4 h-4 transition-transform" />
          ) : (
            <ChevronRight className="w-4 h-4 transition-transform" />
          )}
        </div>
      </button>

      {isOpen && (
        <div id={contentId} className={cn("border-t p-4", contentClassName)}>
          {children}
        </div>
      )}
    </div>
  )
}
